import { Theme, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { resolveDestination, to } from "routing/to";
import { useNavigationConfig, useQuestionnaireCompleted } from "../../../hooks";
import { useLastVisitedShopId } from "../../../hooks/useLastVisitedShopId";
import { useAccessibleShops } from "../../../hooks/useAccessibleShops";
import { useCurrentUser } from "../../../context/user";
import { useCurrentShop } from "../../../context/shop";
import { AccountButton } from "../AccountButton";
import { AccountDrawer } from "../AccountDrawer";
import { AccountMenu } from "../AccountMenu";
import { HelpButton } from "../HelpButton";
import { LanguageSwitch } from "../LanguageSwitch";
import { NavigationBarButton } from "../NavigationBarButton";
import { NavigationDrawer } from "../NavigationDrawer/NavigationDrawer";
import { NavigationMenuButton } from "../NavigationMenuButton";
import { UptainLogo } from "../UptainLogo";
import {
  AppBar,
  CenteredLogoWrapper,
  NavigationBarWrapper,
  NavigationButtonsWrapper,
  SettingsWrapper,
  Toolbar
} from "./NavigationBar.styles";

type NavigationBarProps = { withShopNavigation?: boolean };

const NAVIGATION_DRAWER_ID = "navigation-drawer";

const NavigationBarContent = ({
  withShopNavigation = false,
  shopId,
  questionnaireIncomplete = false
}: NavigationBarProps & { shopId?: number; questionnaireIncomplete?: boolean }) => {
  const desktop = useMediaQuery(({ breakpoints }: Theme) => breakpoints.up("xl"));
  const { t } = useTranslation();
  const user = useCurrentUser();
  const navigationRoutes = useNavigationConfig();
  const dashboardPath = resolveDestination(to.dashboard, shopId) ?? to.root;
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [navigationDrawerOpen, setNavigationDrawerOpen] = useState(false);

  const handleOpenNavigation = useCallback(() => {
    setNavigationDrawerOpen((state) => !state);
    setAccountAnchor(null);
  }, []);

  const handleCloseNavigation = useCallback(() => {
    setNavigationDrawerOpen(false);
  }, []);

  const handleOpenAccount = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAccountAnchor(accountAnchor ? null : event.currentTarget);
      setNavigationDrawerOpen(false);
    },
    [accountAnchor]
  );

  const handleCloseAccount = useCallback(() => {
    setAccountAnchor(null);
  }, []);

  const handleDestinationClick = useCallback(() => {
    setAccountAnchor(null);
    setNavigationDrawerOpen(false);
  }, []);

  const logo = (
    <UptainLogo
      to={dashboardPath}
      aria-label={t("navigation.logoLink")}
      onClick={handleDestinationClick}
    />
  );

  return (
    <NavigationBarWrapper>
      <AppBar position="relative">
        <Toolbar>
          {desktop ? (
            logo
          ) : (
            <>
              <NavigationMenuButton
                active={navigationDrawerOpen}
                aria-label={t("navigation.menuButton")}
                aria-controls={NAVIGATION_DRAWER_ID}
                onClick={handleOpenNavigation}
              />
              <CenteredLogoWrapper>{logo}</CenteredLogoWrapper>
            </>
          )}
          {desktop ? (
            <NavigationButtonsWrapper>
              {navigationRoutes.map((route) => (
                <NavigationBarButton
                  key={route.translationKey}
                  route={route}
                  onClick={handleDestinationClick}
                  shopId={shopId}
                />
              ))}
            </NavigationButtonsWrapper>
          ) : null}
          <SettingsWrapper>
            <LanguageSwitch />
            <HelpButton />
            <AccountButton
              showBadge={questionnaireIncomplete}
              active={Boolean(accountAnchor)}
              onClick={handleOpenAccount}
            />
            {desktop ? (
              <AccountMenu
                anchorEl={accountAnchor}
                onClose={handleCloseAccount}
                onNavigate={handleDestinationClick}
                shopId={shopId}
                userId={user.id}
                withShopNavigation={withShopNavigation}
                questionnaireIncomplete={questionnaireIncomplete}
              />
            ) : null}
          </SettingsWrapper>
        </Toolbar>
      </AppBar>
      {!desktop ? (
        <>
          <AccountDrawer
            open={Boolean(accountAnchor)}
            onClose={handleCloseAccount}
            onNavigate={handleDestinationClick}
            shopId={shopId}
            userId={user.id}
            withShopNavigation={withShopNavigation}
            questionnaireIncomplete={questionnaireIncomplete}
          />
          <NavigationDrawer
            id={NAVIGATION_DRAWER_ID}
            routes={navigationRoutes}
            open={navigationDrawerOpen}
            onClose={handleCloseNavigation}
            onNavigate={handleDestinationClick}
            shopId={shopId}
          />
        </>
      ) : null}
    </NavigationBarWrapper>
  );
};

// Only this data adapter needs ShopContext. Both paths render the same navbar UI.
const CurrentShopNavigation = () => {
  const { shopId } = useCurrentShop();
  const questionnaireIncomplete = !useQuestionnaireCompleted();
  const { rememberShop } = useLastVisitedShopId();
  useEffect(() => {
    rememberShop(shopId);
  }, [shopId, rememberShop]);
  return (
    <NavigationBarContent
      withShopNavigation
      shopId={shopId}
      questionnaireIncomplete={questionnaireIncomplete}
    />
  );
};

export const NavigationBar = ({ withShopNavigation = false }: NavigationBarProps) => {
  const { lastVisitedShopId } = useLastVisitedShopId();
  return withShopNavigation ? (
    <CurrentShopNavigation />
  ) : lastVisitedShopId !== undefined ? (
    <NavigationBarContent shopId={lastVisitedShopId} />
  ) : (
    <FirstShopNavigation />
  );
};

const FirstShopNavigation = () => {
  const { data: shops } = useAccessibleShops();
  return <NavigationBarContent shopId={shops?.[0]?.shopId} />;
};
